import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ApmErrorHandler, ApmModule, ApmService } from '@elastic/apm-rum-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideLoadingBar } from '@ngx-loading-bar/core';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { provideLoadingBarRouter } from '@ngx-loading-bar/router';
import { provideMissingTranslationHandler, provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideAuthentication, UnauthenticatedInterceptor } from '@warema/shared/authentication/api';
import { provideUserCentrics } from '@warema/shared/consent';
import { ConsoleMissingTranslationHandler, provideDeviceInfo, provideSnackbar } from '@warema/shared/helpers';
import { AssetCachingInterceptor } from '@warema/shared/interceptors';
import { WaremaTranslationLoader } from '@warema/shared/loaders';
import { APP_CONTEXT, AppContextToken, ENVIRONMENT, StorageConstants } from '@warema/shared/models';
import { SharedPolicyModule } from '@warema/shared/policy';
import { provideSunCenterPrinting } from '@warema/shared/services';
import { AppConfigService } from '@warema/shared/tenant-config';
import { AppStateService } from '@warema/warema-plus/scaffold';
import { APP_CONFIG_SERVICE } from '@warema/warema-plus/tenant';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { MarkdownModule } from 'ngx-markdown';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { reducers } from './store/reducers';

function initializeApp(appConfigService: AppConfigService): () => Promise<void> {
    return () => appConfigService.initializeAppTenantData(localStorage.getItem(StorageConstants.COUNTRY_CODE));
}

function translateLoaderFactory(): WaremaTranslationLoader {
    return new WaremaTranslationLoader(inject(HttpClient));
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(appRoutes),
        importProvidersFrom(BrowserModule),
        importProvidersFrom(BrowserAnimationsModule),
        provideHttpClient(withInterceptorsFromDi()),
        provideAuthentication(environment),
        provideTranslateService({
            loader: provideTranslateLoader(translateLoaderFactory),
            missingTranslationHandler: provideMissingTranslationHandler(ConsoleMissingTranslationHandler),
        }),
        importProvidersFrom(SharedPolicyModule.forRoot()),
        { provide: ENVIRONMENT, useValue: environment },
        { provide: APP_CONTEXT, useValue: AppContextToken.WaremaPlus },
        { provide: HTTP_INTERCEPTORS, useClass: AssetCachingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: UnauthenticatedInterceptor, multi: true },
        { provide: ErrorHandler, useClass: ApmErrorHandler },
        provideDeviceInfo(),
        provideUserCentrics(),
        provideSnackbar(),
        provideSunCenterPrinting(),

        ApmService,
        importProvidersFrom(ApmModule),
        provideLoadingBar({ latencyThreshold: 100 }),
        provideLoadingBarInterceptor(),
        provideLoadingBarRouter(),

        { provide: APP_CONFIG_SERVICE, useValue: AppConfigService },
        AppConfigService,
        provideAppInitializer(() => {
            const initializerFn = initializeApp(inject(AppConfigService));
            return initializerFn();
        }),
        AppStateService,
        DatePipe,
        importProvidersFrom(StoreModule.forRoot(reducers)),
        importProvidersFrom(EffectsModule.forRoot([])),
        environment.production
            ? []
            : importProvidersFrom(StoreDevtoolsModule.instrument({ connectInZone: true, name: 'WaremaPlus' })),
        importProvidersFrom(MarkdownModule.forRoot()),
        importProvidersFrom(NgxExtendedPdfViewerModule),
    ],
};
